// lib/hooks.js  — shared hooks used across components

import React, { useState, useEffect, useRef } from "react";
import { clsx } from "clsx"; // tiny utility — or replace with template literal

/* ── Scroll reveal ── */
export function useScrollReveal(options = {}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { setVisible(true); obs.disconnect(); }
      },
      { threshold: 0.08, rootMargin: "0px 0px -40px 0px", ...options }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return [ref, visible];
}

/* ── Typewriter ── */
export function useTyping(prompts) {
  const [display, setDisplay] = useState("");
  const index = useRef(0);       // which prompt
  const char  = useRef(0);       // which character
  const phase = useRef("typing"); // "typing" | "pausing" | "deleting"

  useEffect(() => {
    function tick1() {
      const current = prompts[index.current];

      if (phase.current === "typing") {
        char.current++;
        setDisplay(current.slice(0, char.current));
        if (char.current === current.length) {
          phase.current = "pausing";
          return 1800; // pause before deleting
        }
        return 60;

      } else if (phase.current === "pausing") {
        phase.current = "deleting";
        return 35;

      } else {
        // deleting
        char.current--;
        setDisplay(current.slice(0, char.current));
        if (char.current === 0) {
          index.current = (index.current + 1) % prompts.length;
          phase.current = "typing";
          return 400; // brief pause before next prompt
        }
        return 35;
      }
    }

    function tick() {
      const current = prompts[index.current];

      if (phase.current === "typing") {
        char.current++;
        setDisplay(current.slice(0, char.current));

        if (char.current === current.length) {
          phase.current = "pausing";
          return 1800; // pause after full text
        }

        return 60;

      } else {
        // pausing → go to next prompt directly
        index.current = (index.current + 1) % prompts.length;
        char.current = 0;
        phase.current = "typing";

        return 400; // delay before next typing starts
      }
    }
    let timeoutId;
    function schedule() {
      const delay = tick();
      timeoutId = setTimeout(schedule, delay);
    }
    timeoutId = setTimeout(schedule, 60);
    return () => clearTimeout(timeoutId);
  }, []); // runs once, self-scheduling loop

  return display;
}

/* ── Reveal wrapper component ── */

export function Reveal({ children, className = "" }) {
  const [ref, visible] = useReveal();

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${
        visible
          ? "opacity-100 translate-y-0"
          : "opacity-0 translate-y-6"
      } ${className}`}
    >
      {children}
    </div>
  );
}

export function useReveal(options = {}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el); // trigger once
        }
      },
      {
        threshold: 0.1,
        rootMargin: "0px 0px -40px 0px",
        ...options,
      }
    );

    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  return [ref, visible];
}